import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { queryClient } from "../../lib/queryClient";
import api, { isAbortError } from "../../utils/api";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { type Notification } from "../../store/lite/notificationsStore";

const ITEMS_PER_PAGE = 8;

interface RawNotification {
  id: string;
  title?: string;
  description?: string;
  priority?: Notification["priority"];
  status?: string;
  icon?: string;
  image?: string | null;
  createdAt?: Notification["createdAt"];
  readed?: boolean;
  url?: string | null;
}

interface NotificationsListBody {
  data?: RawNotification[];
  meta?: { totalPages?: number; currentPage?: number };
}

export interface LiteNotificationsPage {
  items: Notification[];
  currentPage: number;
  totalPages: number;
}

const mapNotifications = (raw: RawNotification[]): Notification[] =>
  raw
    .filter((n) => n.title && n.description && n.readed !== true)
    .map((n) => ({
      id: n.id,
      title: n.title || "Sin título",
      description: n.description || "",
      priority: n.priority || "medium",
      status: n.status || "sent",
      icon: n.icon || "notification",
      image: n.image || null,
      createdAt: n.createdAt || { _seconds: 0, _nanoseconds: 0 },
      isRead: n.readed === true,
      url: n.url ?? null,
    }));

export function useLiteNotifications() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId;

  const query = useInfiniteQuery({
    queryKey: qk.liteNotifications(cid),
    queryFn: ({ pageParam, signal }) =>
      getJson<NotificationsListBody>(
        "/notifications/inbox",
        { limit: ITEMS_PER_PAGE, page: pageParam, readed: false },
        { signal },
      ).then(
        (body): LiteNotificationsPage => ({
          items: mapNotifications(body.data ?? []),
          currentPage: body.meta?.currentPage || 1,
          totalPages: body.meta?.totalPages || 1,
        }),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
  });

  const items = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.items),
    [query.data],
  );

  return {
    items,
    unreadCount: items.length,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

export function removeLiteNotificationFromCache(notificationId: string) {
  queryClient.setQueriesData<InfiniteData<LiteNotificationsPage>>(
    { queryKey: ["liteNotifications"] },
    (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              items: p.items.filter((n) => n.id !== notificationId),
            })),
          }
        : old,
  );
}

export function clearLiteNotificationsCache() {
  queryClient.setQueriesData<InfiniteData<LiteNotificationsPage>>(
    { queryKey: ["liteNotifications"] },
    (old) =>
      old
        ? { ...old, pages: old.pages.map((p) => ({ ...p, items: [] })) }
        : old,
  );
}

export async function markLiteNotificationRead(notificationId: string) {
  removeLiteNotificationFromCache(notificationId);
  try {
    await api.put("/notifications-read", { notification_id: notificationId });
  } catch (err) {
    if (!isAbortError(err))
      console.warn("Error marking notification as read:", err);
  }
}

export async function markAllLiteNotificationsRead() {
  clearLiteNotificationsCache();
  try {
    await api.put("/notifications-read-all");
  } catch (err) {
    if (!isAbortError(err))
      console.warn("Error marking all notifications as read:", err);
  } finally {
    queryClient.invalidateQueries({ queryKey: ["liteNotifications"] });
  }
}
